import React, { useState, useEffect } from "react";
import {
  Button,
  Box,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  Typography,
} from "@mui/material";
import CustomTextField from "../Fields/Textfield";
import { RefreshOutlined, CheckOutlined, CloseOutlined } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import BDrawer from "../BDrawer";
import { InfoCircle, Repeat } from "iconsax-react";

export interface DuplicateNameDialogProps {
  open: boolean;
  onClose: () => void;
  onReplace: () => void;
  onContinue: () => void;
  onRename?: (newName: string) => void;
  existingName: string;
  newName?: string;
  title?: string;
  message?: string;
}

const DuplicateNameDialog: React.FC<DuplicateNameDialogProps> = ({
  open,
  onClose,
  onReplace,
  onContinue,
  onRename,
  existingName,
  newName,
  title,
  message,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const resolvedTitle = title ?? t("components.historyDrawer.duplicateDialog.title");
  const resolvedMessage = message ?? t("components.historyDrawer.duplicateDialog.message");
  const [shouldRename, setShouldRename] = useState(false);
  const [renameValue, setRenameValue] = useState(newName || existingName);

  useEffect(() => {
    if (open) {
      setShouldRename(false);
      setRenameValue(newName || existingName);
    }
  }, [open, newName, existingName]);

  const handleReplace = () => {
    onReplace();
    handleClose();
  };

  const handleContinue = () => {
    onContinue();
    handleClose();
  };

  const handleRename = () => {
    if (onRename && renameValue.trim()) {
      onRename(renameValue.trim());
    }
    handleClose();
  };

  const handleClose = () => {
    setShouldRename(false);
    setRenameValue(newName || existingName);
    onClose();
  };

  // Action buttons component
  const actionButtons = (
    <>
      <Button
        onClick={shouldRename ? handleRename : handleReplace}
        startIcon={<Repeat color={theme.palette.error.main} size={20} />}
        color="error"
        fullWidth
        variant="outlined"
      >
        {shouldRename ? t("components.historyDrawer.renameAction") : t("components.historyDrawer.replaceAction")}
      </Button>
      <Button
        onClick={handleContinue}
        fullWidth
        startIcon={<CheckOutlined color="inherit" />}
        color="inherit"
        disabled={!renameValue.trim()}
        variant="outlined"
      >
        {t("components.historyDrawer.continueAction")}
      </Button>
    </>
  );

  // Bottom drawer for smaller screens
  if (isMobile) {
    return (
      <BDrawer
        open={open}
        onClose={handleClose}
        anchor="bottom"
        title={
          <Box display="flex" alignItems="center" gap={0.5}>
            <InfoCircle variant="Bulk" size={24} color={theme.palette.primary.main} />
            <Box component="span">{resolvedTitle}</Box>
          </Box>
        }
        showTopBar={true}
        showBottomBar={false}
        dialogActions={
          <Box sx={{ display: "flex", gap: 1, width: "100%", px: 1 }}>
            {actionButtons}
          </Box>
        }
      >
        <Box sx={{ pt: 2 }}>
          <Box sx={{ mb: 2 }}>
            <Box component="span" sx={{ color: theme.palette.text.primary }}>
              {resolvedMessage}
            </Box>
          </Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={shouldRename}
                onChange={(e) => setShouldRename(e.target.checked)}
                sx={{
                  color: theme.palette.primary.main,
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                  "&.Mui-checked": {
                    color: theme.palette.primary.main,
                  },
                }}
              />
            }
            label={t("components.historyDrawer.renameDocumentLabel")}
            sx={{ mb: 2, mx: 0, gap: 1 }}
          />
          {shouldRename && (
            <CustomTextField
              mode="light"
              autoFocus
              fullWidth
              label={t("components.historyDrawer.compareNameLabel")}
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
            />
          )}
        </Box>
      </BDrawer>
    );
  }

  // Dialog for larger screens
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={0.5}>
          <InfoCircle variant="Bulk" size={24} color={theme.palette.primary.main} />
          <Typography variant="h5" >
            {resolvedTitle}
          </Typography>
          </Box>
          <CloseOutlined onClick={handleClose} sx={{ cursor: 'pointer' }} />
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Box sx={{ mb: 2 }}>
          <Box component="span" sx={{ color: theme.palette.text.primary }}>
            {resolvedMessage}
          </Box>
        </Box>
        <FormControlLabel
          control={
            <Checkbox
              checked={shouldRename}
              onChange={(e) => setShouldRename(e.target.checked)}
              sx={{
                color: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: 'transparent',
                },
                "&.Mui-checked": {
                  color: theme.palette.primary.main,
                },
              }}
            />
          }
          label={t("components.historyDrawer.renameDocumentLabel")}
          sx={{ mb: shouldRename ? 2 : 0, mx: 0, gap: 1 }}
        />
        {shouldRename && (
          <CustomTextField
            mode="light"
            autoFocus
            fullWidth
            label={t("components.historyDrawer.compareNameLabel")}
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
          />
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        {actionButtons}
      </DialogActions>
    </Dialog>
  );
};

export default DuplicateNameDialog;

